"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.accountPlanBadges = accountPlanBadges;
exports.accountPlanSummaryLine = accountPlanSummaryLine;
exports.accountPlanPrimaryLabel = accountPlanPrimaryLabel;
exports.accountPlanActiveUntil = accountPlanActiveUntil;
exports.accountPlanActiveUntilLabel = accountPlanActiveUntilLabel;
exports.accountPlanHasTimedAccess = accountPlanHasTimedAccess;
exports.accountPlanActiveUntilText = accountPlanActiveUntilText;
exports.accountPlanOpenPackageLines = accountPlanOpenPackageLines;
const package_catalog_1 = require("./package-catalog");
const package_billing_1 = require("./package-billing");
const package_features_1 = require("./package-features");
const billing_tiers_1 = require("./billing-tiers");
const pro_trial_1 = require("./pro-trial");
const PACKAGE_DISPLAY_ORDER = ['basic', 'pro', 'business'];
function packageLabel(slug) {
    var _a;
    const key = (0, package_features_1.normalizePackageSlug)(slug);
    if (key === 'basic')
        return billing_tiers_1.BASIC_PLAN_LABEL;
    if (key === 'pro' || key === 'business')
        return (0, billing_tiers_1.formatTierLabel)(key);
    const pkg = package_catalog_1.packageCatalog.find((p) => p.slug === key);
    return (_a = pkg === null || pkg === void 0 ? void 0 : pkg.name) !== null && _a !== void 0 ? _a : key;
}
function toIsoOrNull(value) {
    const t = parseTime(value);
    return t === null ? null : new Date(t).toISOString();
}
function pickLatestRow(rows) {
    var _a, _b, _c, _d;
    let best;
    for (const row of rows) {
        if (!best) {
            best = row;
            continue;
        }
        const endA = (_a = parseTime(best.endsAt)) !== null && _a !== void 0 ? _a : Number.POSITIVE_INFINITY;
        const endB = (_b = parseTime(row.endsAt)) !== null && _b !== void 0 ? _b : Number.POSITIVE_INFINITY;
        if (endB > endA)
            best = row;
        else if (endB === endA) {
            const startA = (_c = parseTime(best.startsAt)) !== null && _c !== void 0 ? _c : 0;
            const startB = (_d = parseTime(row.startsAt)) !== null && _d !== void 0 ? _d : 0;
            if (startB >= startA)
                best = row;
        }
    }
    return best;
}
function parentTopSlugForIncluded(catalog, topSlugs, includedSlug) {
    const key = (0, package_features_1.normalizePackageSlug)(includedSlug);
    for (const top of topSlugs) {
        if ((0, package_billing_1.expandPackageIncludes)(catalog, [top]).has(key))
            return top;
    }
    return null;
}
function parseTime(value) {
    if (value == null || value === '')
        return null;
    const t = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
}
function activeTierFromAccount(input) {
    var _a;
    const normalized = (0, billing_tiers_1.normalizeBillingTierId)(input.billingTier);
    if (normalized)
        return normalized;
    const slugs = new Set(((_a = input.activePackages) !== null && _a !== void 0 ? _a : []).map((p) => p.slug));
    if (slugs.has('business') || slugs.has('gold'))
        return 'business';
    if (slugs.has('pro') ||
        slugs.has('silver') ||
        slugs.has('bronze')) {
        return 'pro';
    }
    return null;
}
function tierBadge(tier) {
    return {
        label: (0, billing_tiers_1.formatTierLabel)(tier),
        tone: tier === 'pro' ? 'emerald' : 'gold',
        includes: billing_tiers_1.tierIncludesLabel[tier],
    };
}
/**
 * Ένα (ή δύο σε edge cases) badge: μόνο το ανώτερο πακέτο.
 * Basic / Pro μέσα στο Business δεν εμφανίζονται ξεχωριστά.
 */
function accountPlanBadges(input) {
    var _a;
    if (input.plan === 'FREE' && input.proTrialActive) {
        const days = input.proTrialDaysRemaining;
        const trialLabel = days !== null && days !== undefined
            ? `Δοκιμή Pro · ${days} ${days === 1 ? 'ημέρα' : 'ημέρες'}`
            : 'Δοκιμή Pro';
        return [
            {
                label: trialLabel,
                tone: 'amber',
                includes: billing_tiers_1.tierIncludesLabel.pro,
            },
        ];
    }
    const tier = activeTierFromAccount(input);
    if (tier) {
        return [tierBadge(tier)];
    }
    const slugs = ((_a = input.activePackages) !== null && _a !== void 0 ? _a : []).map((p) => p.slug);
    if ((0, billing_tiers_1.userHasProBase)(input.plan, slugs) || input.plan === 'PRO') {
        return [{ label: billing_tiers_1.BASIC_PLAN_LABEL, tone: 'emerald' }];
    }
    return [{ label: 'FREE', tone: 'violet' }];
}
/** Μία γραμμή για tooltip / aria. */
function accountPlanSummaryLine(input) {
    const badges = accountPlanBadges(input);
    if (badges.length === 0)
        return 'FREE';
    const b = badges[0];
    return b.includes ? `${b.label} — ${b.includes}` : b.label;
}
/** Κύριο όνομα ανοιχτού πακέτου (π.χ. «Business», «Δοκιμή Pro · 5 ημέρες»). */
function accountPlanPrimaryLabel(input) {
    var _a, _b;
    return (_b = (_a = accountPlanBadges(input)[0]) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : 'FREE';
}
/**
 * Ημερομηνία λήξης ανοιχτού πακέτου: User.proEndsAt/tierEndsAt, δοκιμή Pro,
 * ή λήξη κορυφαίας ενεργής συνδρομής (π.χ. admin Business).
 */
function accountPlanActiveUntil(input) {
    var _a, _b;
    const trialActive = (_a = input.proTrialActive) !== null && _a !== void 0 ? _a : (0, pro_trial_1.isProTrialActive)({
        plan: input.plan,
        proTrialEndsAt: input.proTrialEndsAt,
    });
    if (input.plan === 'FREE' && trialActive) {
        const trialEnd = parseTime(input.proTrialEndsAt);
        if (trialEnd !== null)
            return new Date(trialEnd).toISOString();
    }
    const candidates = [];
    for (const field of [input.proEndsAt, input.tierEndsAt]) {
        const t = parseTime(field);
        if (t !== null)
            candidates.push(t);
    }
    const packages = (_b = input.activePackages) !== null && _b !== void 0 ? _b : [];
    if (packages.length > 0) {
        const topSlugs = new Set((0, package_billing_1.filterTopLevelActiveSlugs)(package_catalog_1.packageCatalog, packages.map((p) => p.slug)));
        for (const row of packages) {
            const key = (0, package_features_1.normalizePackageSlug)(row.slug);
            if (!topSlugs.has(key))
                continue;
            const t = parseTime(row.endsAt);
            if (t !== null)
                candidates.push(t);
        }
    }
    if (candidates.length === 0)
        return null;
    return new Date(Math.max(...candidates)).toISOString();
}
/** Ετικέτα πεδίου λήξης (δοκιμή vs πληρωμένη συνδρομή). */
function accountPlanActiveUntilLabel(input) {
    var _a;
    const trialActive = (_a = input.proTrialActive) !== null && _a !== void 0 ? _a : (0, pro_trial_1.isProTrialActive)({
        plan: input.plan,
        proTrialEndsAt: input.proTrialEndsAt,
    });
    if (input.plan === 'FREE' && trialActive)
        return 'Δοκιμή έως';
    return 'Ενεργό έως';
}
/** Έχει πληρωμένη ή δοκιμαστική πρόσβαση (για «Χωρίς λήξη» αντί «—»). */
function accountPlanHasTimedAccess(input) {
    var _a;
    if (input.plan === 'PRO')
        return true;
    if ((0, pro_trial_1.isProTrialActive)({
        plan: input.plan,
        proTrialEndsAt: input.proTrialEndsAt,
    })) {
        return true;
    }
    const slugs = ((_a = input.activePackages) !== null && _a !== void 0 ? _a : []).map((p) => p.slug);
    return (0, billing_tiers_1.userHasProBase)(input.plan, slugs) || activeTierFromAccount(input) !== null;
}
/** Κείμενο για UI· `noDateLabel` όταν δεν υπάρχει ημερομηνία αλλά υπάρχει πρόσβαση. */
function accountPlanActiveUntilText(input, formatDate, options) {
    var _a, _b;
    const iso = accountPlanActiveUntil(input);
    if (iso)
        return formatDate(iso);
    if (accountPlanHasTimedAccess(input)) {
        return (_a = options === null || options === void 0 ? void 0 : options.noDateLabel) !== null && _a !== void 0 ? _a : 'Χωρίς λήξη';
    }
    return (_b = options === null || options === void 0 ? void 0 : options.emptyLabel) !== null && _b !== void 0 ? _b : '—';
}
/**
 * Αναλυτική λίστα ανοιχτών πακέτων για «Τρέχουσα κατάσταση».
 * Κορυφαίες συνδρομές + τι περιλαμβάνονται (με ή χωρίς ξεχωριστή εγγραφή στη βάση).
 */
function accountPlanOpenPackageLines(input) {
    var _a, _b, _c, _d, _e, _f;
    const trialActive = (_a = input.proTrialActive) !== null && _a !== void 0 ? _a : (0, pro_trial_1.isProTrialActive)({
        plan: input.plan,
        proTrialEndsAt: input.proTrialEndsAt,
    });
    if (input.plan === 'FREE' && trialActive) {
        return [
            {
                slug: 'pro-trial',
                label: 'Δοκιμή Pro',
                source: 'trial',
                startsAt: null,
                endsAt: toIsoOrNull(input.proTrialEndsAt),
            },
            {
                slug: 'pro',
                label: packageLabel('pro'),
                source: 'trial',
                startsAt: null,
                endsAt: toIsoOrNull(input.proTrialEndsAt),
            },
            {
                slug: 'basic',
                label: billing_tiers_1.BASIC_PLAN_LABEL,
                source: 'included',
                startsAt: null,
                endsAt: toIsoOrNull(input.proTrialEndsAt),
                viaPackageLabel: 'Δοκιμή Pro',
            },
        ];
    }
    const packages = (_b = input.activePackages) !== null && _b !== void 0 ? _b : [];
    const bySlug = new Map();
    for (const row of packages) {
        const key = (0, package_features_1.normalizePackageSlug)(row.slug);
        const list = (_c = bySlug.get(key)) !== null && _c !== void 0 ? _c : [];
        list.push(row);
        bySlug.set(key, list);
    }
    const topSlugs = (0, package_billing_1.filterTopLevelActiveSlugs)(package_catalog_1.packageCatalog, packages.map((p) => p.slug));
    if (topSlugs.length === 0 && packages.length === 0) {
        if (input.plan === 'PRO' || (0, billing_tiers_1.userHasProBase)(input.plan, [])) {
            return [
                {
                    slug: 'basic',
                    label: billing_tiers_1.BASIC_PLAN_LABEL,
                    source: 'subscription',
                    startsAt: null,
                    endsAt: toIsoOrNull((_d = input.proEndsAt) !== null && _d !== void 0 ? _d : input.tierEndsAt),
                },
            ];
        }
        return [
            {
                slug: 'free',
                label: 'FREE',
                source: 'free',
                startsAt: null,
                endsAt: null,
            },
        ];
    }
    const activeSlugs = new Set();
    for (const top of topSlugs) {
        for (const s of (0, package_billing_1.expandPackageIncludes)(package_catalog_1.packageCatalog, [top])) {
            activeSlugs.add(s);
        }
    }
    const lines = [];
    for (const slug of PACKAGE_DISPLAY_ORDER) {
        if (!activeSlugs.has(slug))
            continue;
        const isTop = topSlugs.includes(slug);
        const row = pickLatestRow((_e = bySlug.get(slug)) !== null && _e !== void 0 ? _e : []);
        const viaTop = !isTop
            ? parentTopSlugForIncluded(package_catalog_1.packageCatalog, topSlugs, slug)
            : null;
        lines.push({
            slug,
            label: packageLabel(slug),
            source: isTop && row ? 'subscription' : 'included',
            startsAt: toIsoOrNull(row === null || row === void 0 ? void 0 : row.startsAt),
            endsAt: toIsoOrNull((_f = row === null || row === void 0 ? void 0 : row.endsAt) !== null && _f !== void 0 ? _f : (isTop ? (slug === 'basic' ? input.proEndsAt : input.tierEndsAt) : null)),
            viaPackageLabel: !isTop && viaTop ? packageLabel(viaTop) : undefined,
        });
    }
    return lines;
}
